/**
 * grid-vip-pricing.js
 *
 * Handles the compact "VIP members pay $X — Become a VIP member" upsell
 * link rendered on collection-grid cards by product-grid-item.liquid
 * (data-grid-vip-upsell-button). This is the collection-grid counterpart
 * to block-vip-pricing.js on the product page.
 *
 * Why this can't reuse the product-page approach directly:
 * The product page injects hidden items[0][id]/items[0][quantity] fields
 * into the page's single real add-to-cart <form> and clicks its native
 * submit button, because that's the only way to trigger the theme's own
 * click-bound AJAX handler and have it open the cart panel correctly (see
 * block-vip-pricing.js for the full story on why simulating a submit event
 * doesn't work). A collection grid has no such single form — each of the
 * many product cards has, at most, its own separate quick-add mechanism
 * with different DOM expectations per variant count (see the long
 * IMPORTANT/IMPORTANT #2 comments in product-grid-item.liquid about
 * quick-add.js's fragile assumptions). Piggybacking on any one card's
 * quick-add would be fragile and wouldn't generalize.
 *
 * So here we go straight to Shopify's Ajax Cart API (POST /cart/add.js)
 * with both line items in one request. This does NOT open the theme's
 * cart panel automatically or refresh its contents (that only happens via
 * the native click-bound handler on a real add-to-cart button, see
 * cart-drawer.js/cart-form.js), so after a successful add we drive the
 * theme's own drawer through its documented "dev-friendly" DOM events
 * instead of reaching into its internals:
 *   - 'cart:build' — cart-form.js's CartForm listens for this and calls
 *     buildCart(), which fetches /cart?view=ajax fresh and re-renders the
 *     drawer's item list, subtotal, discounts, and count bubble. This is
 *     the same rebuild HeaderCart.handleCartChange() would trigger via the
 *     ajaxProductAdded pubsub event on a native add — but buildCart() takes
 *     no payload and always re-fetches from the server, so dispatching the
 *     plain event gets identical results without needing the pubsub module.
 *   - 'cart:open' — cart-drawer.js's HeaderCart listens for this and calls
 *     open(), sliding the drawer into view.
 * Firing 'cart:build' first and waiting for it to finish before 'cart:open'
 * avoids the drawer opening on stale (or empty) contents, which is what a
 * generic 'cart:updated' event and a bare drawer.open() call both missed.
 * We also still refresh the visible grid-card pricing via a section fetch
 * (same ?sections= pattern as block-vip-pricing.js), so the shopper sees
 * correct pricing on the page itself, not just inside the drawer.
 */
(function () {
  'use strict';

  function onUpsellClick(event) {
    // The whole price/meta block this button lives in sits INSIDE the grid
    // card's ancestor <a class="grid-item__link"> (it wraps the image plus
    // the entire meta/price section). Without stopping the click here, it
    // bubbles up to that anchor and the browser's default navigation to the
    // product page fires — even though the button itself is type="button" —
    // because type="button" only suppresses form-submit behavior, not link
    // navigation from a bubbled click. Must be the first thing in the handler,
    // before any early returns, so it always applies.
    event.preventDefault();
    event.stopPropagation();

    var button = event.currentTarget;
    var wrapper = button.closest('[data-grid-vip-upsell]');
    if (!wrapper) {
      return;
    }

    var membershipVariantId = wrapper.getAttribute('data-membership-variant-id');
    var productVariantId = wrapper.getAttribute('data-product-variant-id');
    var sectionId = wrapper.getAttribute('data-section-id');
    var errorEl = wrapper.querySelector('[data-grid-vip-upsell-error]');
    var labelEl = button.querySelector('[data-grid-vip-upsell-label]');
    var originalLabel = labelEl ? labelEl.textContent : null;

    if (!membershipVariantId || !productVariantId) {
      return;
    }

    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }
    button.disabled = true;
    if (labelEl) {
      labelEl.textContent = 'Adding…';
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [
          { id: membershipVariantId, quantity: 1 },
          { id: productVariantId, quantity: 1 },
        ],
      }),
    })
      .then(function (res) {
        if (!res.ok) {
          return res.json().then(function (data) {
            throw new Error((data && data.description) || 'Could not add to cart.');
          });
        }
        return res.json();
      })
      .then(function () {
        return openCartDrawerWithFreshContents().then(function () {
          return refreshSection(sectionId);
        });
      })
      .catch(function (err) {
        if (errorEl) {
          errorEl.hidden = false;
          errorEl.textContent =
            (err && err.message) ||
            "Couldn't add the membership. Please try again or contact us.";
        }
      })
      .finally(function () {
        restoreButton(button, labelEl, originalLabel);
      });
  }

  function openCartDrawerWithFreshContents() {
    // CartForm (cart-form.js) listens for 'cart:build' and calls
    // buildCart(), which fetches /cart?view=ajax and re-renders the
    // drawer's item list/subtotal/discounts/count bubble. HeaderCart
    // (cart-drawer.js) listens for 'cart:open' and calls open(). Neither
    // listener reads anything off the event object, so no detail/payload
    // is needed — dispatching the bare events is enough to match what a
    // native add-to-cart submit triggers.
    //
    // There's no direct signal for "buildCart() finished" from a DOM
    // event alone, so we pair the dispatch with the same /cart?view=ajax
    // request cart-form.js itself makes (getCartProductMarkup) and wait
    // on that before opening the drawer. This guarantees the server has
    // returned the updated cart (with both line items) before the drawer
    // becomes visible, avoiding the "opens empty" race.
    document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));

    var url = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root + 'cart?view=ajax&t=' + Date.now()
      : '/cart?view=ajax&t=' + Date.now();

    return fetch(url, { credentials: 'same-origin' })
      .catch(function () {
        // Best effort only — if this probe fails, still try to open the
        // drawer; cart:build's own fetch may still succeed independently.
      })
      .then(function () {
        document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
      });
  }

  function refreshSection(sectionId) {
    if (!sectionId) {
      return Promise.resolve();
    }
    var separator = window.location.search ? '&' : '?';
    var url =
      window.location.pathname +
      window.location.search +
      separator +
      'sections=' +
      encodeURIComponent(sectionId);

    return fetch(url)
      .then(function (res) {
        return res.json();
      })
      .then(function (data) {
        var html = data[sectionId];
        if (!html) {
          throw new Error('Section response missing expected data.');
        }
        var doc = new DOMParser().parseFromString(html, 'text/html');

        // Swap every grid card's price/upsell block, matched by product
        // id, rather than the whole section — this preserves scroll
        // position and any per-card UI state (e.g. an open quick-shop
        // modal) elsewhere on the page.
        var freshCards = doc.querySelectorAll('[data-product-grid-item]');
        freshCards.forEach(function (freshCard) {
          var productId = freshCard.getAttribute('data-product-id');
          if (!productId) {
            return;
          }
          var currentCard = document.querySelector(
            '[data-product-grid-item][data-product-id="' + productId + '"]'
          );
          if (!currentCard) {
            return;
          }
          var freshMeta = freshCard.querySelector('.grid-item__meta-secondary');
          var freshUpsell = freshCard.querySelector('[data-grid-vip-upsell]');
          var currentMeta = currentCard.querySelector('.grid-item__meta-secondary');
          var currentUpsell = currentCard.querySelector('[data-grid-vip-upsell]');

          if (freshMeta && currentMeta) {
            currentMeta.replaceWith(freshMeta);
          }
          // The upsell line sits as a sibling after .grid-item__meta-secondary,
          // not inside it, so it's swapped (or removed, if the shopper now
          // qualifies for member pricing) separately.
          if (currentUpsell) {
            if (freshUpsell) {
              currentUpsell.replaceWith(freshUpsell);
              bindUpsellButtons(freshUpsell);
            } else {
              currentUpsell.remove();
            }
          }
        });
      })
      .catch(function () {
        // Section refresh failed — the cart add itself already succeeded,
        // so fall back to a full reload rather than leaving prices stale.
        window.location.reload();
      });
  }

  function restoreButton(button, labelEl, originalLabel) {
    if (!button || !button.isConnected) {
      return;
    }
    button.disabled = false;
    if (labelEl && originalLabel) {
      labelEl.textContent = originalLabel;
    }
  }

  function bindUpsellButtons(scope) {
    var root = scope || document;
    var buttons = root.querySelectorAll('[data-grid-vip-upsell-button]');
    buttons.forEach(function (button) {
      button.addEventListener('click', onUpsellClick);
    });
  }

  function init() {
    bindUpsellButtons(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();